image.png<template>
  <div
    class="product-options-action"
    :class="{'--hide-pc-price': isHidePcPrice && !isShowBonusRowPrice}"
  >
    <template v-if="isHasOptions">
      <div class="product-options-action__options">
        <div class="--label h6">Выберите комплектацию:</div>
        <div class="--list">
          <SelectOption
            v-for="(option, index) in listOptions"
            :key="`option-${option.id}`"
            :product="product"
            :option="option"
            :product-slug="product.slug"
            :value="selectedOptions[option.id]"
            :error="selectedOptionsError[option.id]"
            :selected="selectedOptions"
            :disabledMessage="checkDisabledOption(option, index)"
            @change="(variant) => changeSelectedOptions(option, variant)"
          />
        </div>
      </div>
    </template>
    <template v-if="isShowBonusRowPrice">
      <div class="product-options-action__bonus-price">
        <div class="product-options-action__bonus-card">
          {{ labelForBonusCard }}
        </div>
        <div class="product-options-action__prices --all">
          <OptionPriceUpdateHint
            :message="priceUpdateHintMessage"
          />
          <div
            v-if="oldPriceProduct"
            class="--old"
          >
            <span v-if="addedLowerLimitPricePhrases && isHasOptions">от</span>
            {{ nuxtApp.$numberFormat(oldPriceProduct) }} ₽
          </div>
          <div class="--current h2">
            <span v-if="addedLowerLimitPricePhrases && isHasOptions">от</span>
            {{ nuxtApp.$numberFormat(priceProduct) }} ₽
          </div>
        </div>
      </div>
    </template>

    <div v-if="!isShowBonusRowPrice" class="product-options-action__prices">
      <OptionPriceUpdateHint
        :message="priceUpdateHintMessage"
      />
      <div
        v-if="oldPriceProduct"
        class="--old"
      >
        <span v-if="addedLowerLimitPricePhrases && isHasOptions">от</span>
        {{ nuxtApp.$numberFormat(oldPriceProduct) }} ₽
      </div>
      <div class="--current h2">
        <span v-if="addedLowerLimitPricePhrases && isHasOptions">от</span>
        {{ nuxtApp.$numberFormat(priceProduct) }} ₽
      </div>
    </div>
    <div class="product-options-action__add-to-cart">
      <AddToCartButtonWithCounter
        :selected-options="selectedOptions"
        :product="product"
        :is-reset-settings="isResetAddToCartBtn"
        @errorList="checkError"
      />
    </div>
    <div
      class="product-options-action__cheaper"
      @click="openPopupDefault"
    >
      <div>Напишите, если нашли дешевле</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, type ComputedRef, ref} from 'vue';
import SelectOption from '~/components/options/SelectOption.vue';
import OptionPriceUpdateHint from '~/components/options/OptionPriceUpdateHint.vue';
import useCart from '#sitis/composables/useSitisCart';
import {useCommonStore} from '~/store/common';
import type {IProduct, ProductCharacteristics, ProductCharacteristic} from '#sitis/internal/controllers/products/models/Product';
import {useDefaultPopup} from '#imports';
import AddToCartButtonWithCounter from '~/components/widget/AddToCartButtonWithCounter.vue';
import { getTotalOldPriceOptions, getTotalPriceOptions, getDependentPriceUpdateHintMessage } from '~/utils/productOptions';

type ProductCarBodyType = {
	quantity: number;
	productId: number;
	options: { [key: string]: any }
}
const props = defineProps<{
	product: IProduct
}>();
const { openDefaultPopup } = useDefaultPopup();
const nuxtApp = useNuxtApp();
const cartStore = useCart();
const commonStore = useCommonStore();

// Информация по ценам
const priceProduct = computed(() => {
  const optionsPrice = getTotalPriceOptions(selectedOptions.value, props.product.price, listOptions.value);
  return props.product.price + optionsPrice;
});
const oldPriceProduct = computed(() => {
  if (!props.product.oldPrice || props.product.oldPrice <= props.product.price) {
    return null;
  }
  const optionsPrice = getTotalOldPriceOptions(selectedOptions.value, props.product.oldPrice, listOptions.value);
  return props.product.oldPrice + optionsPrice;
});


const labelForBonusCard = computed(() => {
  const optionsValues = Object.keys((selectedOptions.value || {})).reduce((value, key) => {
    const price = selectedOptions.value?.[key]?.bonus_value || 0;
    return value + price;
  }, 0);
  const _value: number = Math.floor((props.product?.dynamicFields?.bonus_value_total_exact || 0) + (optionsValues || 0));

  const _word: string = nuxtApp.$caseWords(_value, ['бонус','бонуса','бонусов']);
  return `+ ${ _value } ${ _word }  на счет`;
});
const isShowBonusRowPrice: ComputedRef<boolean> = computed(() => {
  return Boolean(props.product?.totalBonusValue)
});

// Логика по опциям товара
const isHasOptions = computed(() => props.product?.hasOptions);
const listOptions = computed(() => {
  return props.product?.options || []
});
const selectedOptions = ref<{ [key: string]: any }>({});
const selectedOptionsError = ref<{ [key: number]: string }>({});
const priceUpdateHintMessage = ref('');
const isResetAddToCartBtn = ref<boolean>(false);
const countOfAddedProduct = ref<number>(0);
const getKeyForSelectOption = (option: any) => {
  const _selectedKey = Object.keys(selectedOptions.value || {}).map((key: string) => `${key}_${(selectedOptions.value || {})?.[key]?.['id']}`).join('-');
  return `${option?.id}-${_selectedKey}`
}

const checkError = (errors: { [key: number]: string }) => {
  if (countOfAddedProduct.value > 0) {
    isResetAddToCartBtn.value = false;
  }

  countOfAddedProduct.value++;
  selectedOptionsError.value = errors;
};
const checkDisabledOption = (option: any, index: any) => {
  return ''
}
const changeSelectedOptions = (option: any, variant: any) => {
  selectedOptions.value[option.id] = variant;
  priceUpdateHintMessage.value = getDependentPriceUpdateHintMessage(
    option,
    selectedOptions.value,
    listOptions.value
  );
};

function openPopupDefault () {
  openDefaultPopup({
    title: 'Нашли дешевле?',
    isNeedUploadImages: true
  });
}

const isHidePcPrice: ComputedRef<boolean> = computed(() => {
  return !Boolean(props?.product?.hasOptions)
});
const addedLowerLimitPricePhrases = computed(() => {
  let _findCharGroup:ProductCharacteristics | undefined = props?.product?.characteristics.find((charGroup: ProductCharacteristics) => {
    return charGroup.characteristics.find((char: ProductCharacteristic) => char.slug === 'add-to-price')
  })
  if (_findCharGroup && _findCharGroup.characteristics) {
    const _index = _findCharGroup.characteristics.findIndex((char) => char.slug === 'add-to-price')
    return _findCharGroup?.characteristics?.[_index]?.values?.[0]?.value === 'да'
  }
})
</script>

<style scoped lang="scss">
.product-options-action {}

.product-options-action__options {
	margin-bottom: 28px;

	.--label {
		color: #000000;
	}

	.--list {
		margin-top: 10px;

		& > * {
			margin-top: 6px;

			&:first-child {
				margin-top: 0;
			}
		}
	}
}

.product-options-action__bonus-price {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.product-options-action__prices {
	display: flex;
	align-items: center;
	justify-content: flex-end;

	:deep(.option-price-update-hint) {
		margin-right: 8px;
	}

	.--old {
		margin-right: 6px;
		color: #B8B8B8;
		font-variant-numeric: lining-nums proportional-nums;
		font-size: 12px;
		font-weight: 500;
		line-height: 22px;
		text-decoration: line-through;
	}

	.--current {
		color: #000000;
		line-height: 100%;
	}
}

.product-options-action__add-to-cart {
	margin-top: 19px;

	.btn {
		width: 100%;
		height: 60px;
		border-radius: 16px;

		text-align: center;
		font-weight: 600;
	}
}

.product-options-action__cheaper {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 13px;

	div {
		color: #989898;
		font-size: 12px;
		font-weight: 500;
		text-decoration: underline;
		transition: all 0.3s;
		cursor: pointer;

		&:hover {
			text-decoration-color: transparent;
		}
	}
}

.product-options-action__bonus-card {
  background: #D3E6C6;
  border-radius: 7px;
  padding: 4px 12px;
  box-sizing: border-box;

  color: #333333;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 600;
}

@media (min-width: 1024px) {
  .product-options-action {
    &.--hide-pc-price .product-options-action__prices {
      display: none;
    }
  }
}
@media (max-width: 1023px) {
	.product-options-action__prices {
		.--old {
			line-height: 15px;
		}

		.--current {
			font-size: 20px;
		}
	}
	.product-options-action__add-to-cart {
		.btn {
			height: 48px;
			border-radius: 12px;
		}
	}
}
</style>
